import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import {
    FaFacebook,
    FaInstagram,
    FaLinkedin,
    FaPinterest,
    FaXTwitter,
    FaYoutube,
} from "react-icons/fa6";
import {
    swFade,
    swFloatUp,
    swSlideLeft,
    swSlideRight,
    swViewport,
    swZoom,
} from "../components/software/shared/softwareMotion";
import { smaFeatures, smaMetrics } from "../data/softwarePagesContent";
import "../styles/software-pages-base.css";
import "../styles/socialmedia-automation.css";

const RingMetric = ({ label, value, color }: { label: string; value: number; color: string }) => (
    <motion.div
        className="sma-ring"
        initial="hidden"
        whileInView="visible"
        viewport={swViewport}
        variants={swFloatUp}
    >
        <div
            className="sma-ring__circle"
            style={{
                background: `conic-gradient(${color} ${value * 3.6}deg, #e8ecf4 ${value * 3.6}deg)`,
            }}
        >
            <span>{value}%</span>
        </div>
        <p>{label}</p>
    </motion.div>
);

const platformIcons = [
    { Icon: FaInstagram, label: "Instagram", className: "sma-hero__orbit-icon--ig", delay: 0 },
    { Icon: FaFacebook, label: "Facebook", className: "sma-hero__orbit-icon--fb", delay: 0.35 },
    { Icon: FaYoutube, label: "YouTube", className: "sma-hero__orbit-icon--yt", delay: 0.7 },
    { Icon: FaLinkedin, label: "LinkedIn", className: "sma-hero__orbit-icon--li", delay: 1.05 },
    { Icon: FaXTwitter, label: "X", className: "sma-hero__orbit-icon--x", delay: 0.5 },
    { Icon: FaPinterest, label: "Pinterest", className: "sma-hero__orbit-icon--pinterest", delay: 1.25 },
];

export const SocialMediaAutomationPage = () => {
    const navigate = useNavigate();

    return (
        <div className="sw-page sma-page">
            {/* §1 Hero */}
            <section className="sma-hero">
                <div className="sw-container">
                    <motion.div
                        className="sma-hero__intro"
                        initial="hidden"
                        animate="visible"
                        variants={swFade}
                    >
                        <h1 className="sma-hero__title">Social Media Automation</h1>
                        <h2 className="sma-hero__tagline">Automate publishing. Amplify engagement.</h2>
                        <p className="sma-hero__lead">
                            Schedule, manage, and optimize content across all major social platforms
                            from one intelligent dashboard.
                        </p>
                    </motion.div>

                    <div className="sma-hero__stage">
                        <div className="sma-hero__orbit" aria-label="Connected social platforms">
                            {platformIcons.map(({ Icon, label, className, delay }, index) => (
                                <motion.div
                                    key={label}
                                    className={`sma-hero__orbit-icon ${className}`}
                                    style={{ animationDelay: `${delay}s` }}
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{
                                        duration: 0.5,
                                        delay: 0.35 + index * 0.08,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                    title={label}
                                    aria-label={label}
                                >
                                    <Icon aria-hidden />
                                </motion.div>
                            ))}
                        </div>

                        <motion.div
                            className="sma-hero__mockup"
                            initial="hidden"
                            animate="visible"
                            variants={swZoom}
                        >
                            <div className="sma-hero__mockup-bar">
                                <span>Content Dashboard</span>
                                <span className="sma-hero__mockup-pill">Live</span>
                            </div>
                            <div className="sma-hero__mockup-chart">
                                {[58, 42, 76, 64, 88, 52, 72].map((h, i) => (
                                    <motion.div
                                        key={i}
                                        className="sma-hero__mockup-bar-col"
                                        initial={{ height: 0 }}
                                        animate={{ height: `${h}%` }}
                                        transition={{ delay: 0.2 + i * 0.08, duration: 0.55 }}
                                    />
                                ))}
                            </div>
                            <div className="sma-hero__mockup-rows">
                                <div className="sma-hero__mockup-row">
                                    <span>Instagram Reel</span>
                                    <em>Today · 6:30 PM</em>
                                </div>
                                <div className="sma-hero__mockup-row">
                                    <span>LinkedIn Article</span>
                                    <em>Tomorrow · 9:00 AM</em>
                                </div>
                                <div className="sma-hero__mockup-row">
                                    <span>X Thread</span>
                                    <em>Fri · 11:15 AM</em>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* §2 Smart Features */}
            <section className="sma-features">
                <div className="sw-container">
                    <motion.div
                        className="sma-section-head"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swFloatUp}
                    >
                        <h2 className="sma-h2">Smart Features Showcase</h2>
                        <p className="sma-sub">Purpose-built tools for creators, marketers, and growing brands.</p>
                    </motion.div>
                    {smaFeatures.map((feat, i) => (
                        <motion.div
                            key={feat.title}
                            className={`sma-feature sma-feature--${feat.side}`}
                            initial="hidden"
                            whileInView="visible"
                            viewport={swViewport}
                            variants={feat.side === "left" ? swSlideLeft : swSlideRight}
                            custom={i * 0.05}
                        >
                            <div className="sma-feature__visual">
                                <img
                                    src={feat.image}
                                    alt={feat.alt}
                                    className="sma-feature__dashboard"
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                            <div className="sma-feature__copy">
                                <h3>{feat.title}</h3>
                                <p>{feat.desc}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* §3 Performance Metrics */}
            <section className="sma-metrics">
                <div className="sw-container">
                    <motion.div
                        className="sma-section-head sma-section-head--center"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swFloatUp}
                    >
                        <h2 className="sma-h2 sma-h2--light">Performance Metrics</h2>
                        <p className="sma-sub sma-sub--light">Measurable impact from day one of automation.</p>
                    </motion.div>
                    <div className="sma-metrics__rings">
                        {smaMetrics.map((m) => (
                            <RingMetric key={m.label} {...m} />
                        ))}
                    </div>
                    <motion.div
                        className="sma-metrics__bars"
                        initial="hidden"
                        whileInView="visible"
                        viewport={swViewport}
                        variants={swFloatUp}
                    >
                        {[88, 64, 92, 76].map((w, i) => (
                            <motion.div
                                key={i}
                                className="sma-metrics__bar"
                                initial={{ width: 0 }}
                                whileInView={{ width: `${w}%` }}
                                viewport={swViewport}
                                transition={{ delay: i * 0.1, duration: 0.7 }}
                            />
                        ))}
                    </motion.div>
                </div>
            </section>

            <section className="sw-cta">
                <div className="sw-container">
                    <div className="sw-cta__inner">
                        <h2 className="sw-cta__title">Automate your social presence</h2>
                        <p className="sw-cta__desc">Schedule, publish, and measure — without juggling a dozen tabs.</p>
                        <Button className="sw-btn" onClick={() => navigate("/contact")}>
                            Get Started <ArrowRightOutlined />
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
};
